import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  ApolloLink,
  split,
} from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { getJourneyIdFromUrl } from '@volumental/journey-manager';
import { sendEvent } from '@volumental/link';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { createClient } from 'graphql-ws';
import React, { type PropsWithChildren } from 'react';
import { GeneralErrorPage } from '../pages/error/GeneralErrorPage';
import { getApiURL, getWsApiURL } from '../util/select-api';

const clientId = new URLSearchParams(window.location.search).get('token');
const jid = getJourneyIdFromUrl();

const httpLink = createUploadLink({
  uri: getApiURL(),
  headers: {
    'X-Visa-Jid': `${jid}`,
    'X-Visa-Token': `${clientId}`,
  },
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: getWsApiURL(),
    keepAlive: 10_000,
    connectionParams: {
      'visa-token': `${clientId}`,
    },
  }),
);

const link = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLink as ApolloLink,
);

const client = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      WidgetConfiguration: {
        keyFields: [],
        fields: {
          scanner: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming);
            },
          },
          features: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming);
            },
          },
        },
      },
      ScannerConfiguration: {
        keyFields: false,
      },
      FeatureConfiguration: {
        keyFields: false,
        fields: {
          recommendation: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming);
            },
          },
          measurement: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming);
            },
          },
        },
      },
      RecommendationFlowSettings: {
        keyFields: false,
      },
      MeasurementFlowSettings: {
        keyFields: false,
      },
    },
  }),
  link: ApolloLink.from([link]),
});

export function ApiProvider({
  children,
}: PropsWithChildren): React.JSX.Element {
  const onClose = (): void => {
    sendEvent({
      type: 'close',
      data: undefined,
    });
  };
  if (!clientId) {
    return <GeneralErrorPage errorText="Missing clientId" onClose={onClose} />;
  }
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
